import React, { useEffect } from 'react'
import '../Configs.css'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import { useNavigate } from 'react-router-dom'
import { createConfig, getConfigs, setEntityError } from '../../../redux/slices'
import { useDispatch, useSelector } from 'react-redux'
import ICSTextInput from '../../../components/ICSTextInput/ICSTextInput'
import ICSTextAreaInput from '../../../components/ICSTextAreaInput/ICSTextAreaInput'
import ICSFileInput from '../../../components/ICSFileInput/ICSFileInput'
import { Col, Row } from 'reactstrap'
import Joi from 'joi'
import { joiResolver } from '@hookform/resolvers/joi'
import { FieldValues, useForm } from 'react-hook-form'
import { Form, FormGroup } from 'reactstrap'
import ComponentBoxFooter from '../../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import { AppDispatch } from '../../../services/lib'
import { hasInvalidField, invalidFieldsMessage } from '../../../utils/helpers'
import { DefaultState } from '../../../types'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'

const defaultJoiSchema = {
  files: Joi.array().items(Joi.any()).min(1).max(1).required().messages({
    'array.min': `Required field`,
  }),
  name: Joi.string().required().min(3).max(30).messages({
    'string.empty': `Required field`,
    'string.min': `Must be at lease {#limit} in length`,
  }),
  description: Joi.string().min(0).max(255).allow(''),
}

const schema = Joi.object(defaultJoiSchema)

const ConfigSave = ({ hideModal, scriptId }) => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const configsError = useSelector((state: DefaultState) => state.configs.error)
  const shouldShowErrorCard =
    configsError && (!configsError.fields || configsError.fields.length === 0)

  const {
    formState: { errors, isSubmitted, isValid },
    handleSubmit,
    control,
    setError,
    clearErrors,
  } = useForm<FieldValues>({
    mode: 'onChange',
    resolver: joiResolver(schema),
    defaultValues: {
      files: [],
      name: '',
      description: '',
    },
  })

  useEffect(() => {
    if (!scriptId) return navigate('/ui2/scripts')

    if (configsError) {
      configsError.fields.forEach(async errorObj =>
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message,
        })
      )
    }
  }, [scriptId, navigate, configsError, setError])

  useEffect(() => {
    if (configsError) {
      configsError.fields.forEach(async errorObj =>
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message,
        })
      )
    }
  }, [configsError])

  const readConfigFile = async (data: File) => {
    // TODO: Revisit typing this function
    if (!data) return ''

    const fileText = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsText(data)
      reader.onload = () => {
        resolve(reader.result as string)
      }
      reader.onerror = error => reject(error)
    })

    try {
      JSON.parse(fileText)
      clearErrors('files')
    } catch (error) {
      setError('files', {
        type: 'manual',
        message: 'Invalid Configuration File: JSON syntax error.'
      })
      return ''
    }

    const fileString = new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(data)
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = error => reject(error)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).then((data: any) => {
      return data.substring(data.lastIndexOf(',') + 1, data.length)
    })

    return fileString
  }

  const onSubmit = async formValues => {
    const { files, description, name } = formValues
    const [selectedFile] = files
    const file = await readConfigFile(selectedFile)

    if (!file) return

    // TODO: fix 'any' type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createdConfig: any = await dispatch(
      createConfig({
        id: scriptId,
        body: {
          name,
          description,
          file,
        },
      })
    )

    if (createdConfig.type.split('/').pop() === 'fulfilled') {
      dispatch(getConfigs(scriptId))
      hideModal()
    } else {
      const verifyFields = await hasInvalidField(
        Object.keys(defaultJoiSchema),
        createdConfig.payload.fields.map(
          (fieldObj: { field: string; message: string }) => fieldObj.field
        )
      )

      if (verifyFields.isInvalid) {
        dispatch(
          setEntityError({
            sliceName: 'configs',
            entityName: 'Config',
            error: {
              code: 'error',
              message: invalidFieldsMessage,
              fields: verifyFields.fields,
            },
          })
        )
      }
    }
  }

  return (
    <>
      <Row>
        <Col xs={{ size: 12 }}>
          {shouldShowErrorCard ? (
            <ICSErrorCard
              code={configsError.code}
              message={configsError.message}
              fields={configsError.fields}
            />
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <Row>
                  <Col>
                    <ICSFileInput
                      name='files'
                      label='Upload Configuration'
                      accept={['.asl']}
                      control={control}
                      helpText='Choose an ASL configuration file to upload.'
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ICSTextInput
                      name='name'
                      placeholder='Configuration Name...'
                      maxCharCount={30}
                      control={control}
                      errors={errors}
                      isSubmitted={isSubmitted}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ICSTextAreaInput
                      name='description'
                      placeholder='Configuration Description...'
                      control={control}
                      errors={errors}
                      isSubmitted={isSubmitted}
                    />
                  </Col>
                </Row>
                <ComponentBoxFooter>
                  <ICSButton
                    id='formSubmit'
                    width={60}
                    useSubmitBehavior={true}
                    type='success'
                    disabled={!isValid}
                  >
                    Save
                  </ICSButton>
                </ComponentBoxFooter>
              </FormGroup>
            </Form>
          )}
        </Col>
      </Row>
    </>
  )
}

export default ConfigSave
